import type { ReceivedInvoiceReceiptType } from '@/context/services/invoice/type';
import { useReceiptTypeChecks } from '@/hooks/useReceiptTypeChecks';
import { useTranslation } from '@/i18n';
import {
  Bottom,
  Caption,
  Left,
  ListView,
  Right,
  Top,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import styles from './EDocMetadata.module.scss';

const cx = classnames.bind(styles);

type LeftRightRatio = {
  left: { min: string; max: string };
  right: { min: string; max: string };
};

export type Props = {
  dpi: string;
  pixelNumber: string;
  imageDepth: string;
  type?: 'warning' | 'error';
  isScan: boolean;
  isSatisfyResolutionRequirement: boolean;
  isSatisfyDepthRequirements: boolean;
  receiptType?: ReceivedInvoiceReceiptType;
  leftRightRatio?: LeftRightRatio;
};

export const EDocMetadata: FC<Props> = memo(
  ({
    dpi,
    pixelNumber,
    imageDepth,
    type,
    isScan,
    isSatisfyResolutionRequirement,
    isSatisfyDepthRequirements,
    receiptType,
    leftRightRatio,
  }) => {
    const { t } = useTranslation();
    const { isPaperReceipt } = useReceiptTypeChecks(receiptType);

    // DPI messages
    const dpiAlertMessage = useMemo(() => {
      return !isSatisfyResolutionRequirement && isPaperReceipt
        ? t('e_doc_plan_satisfy_resolution_requirement_alert_label')
        : undefined;
    }, [isSatisfyResolutionRequirement, isPaperReceipt, t]);

    const dpiErrorMessage = useMemo(() => {
      return !isSatisfyResolutionRequirement && isPaperReceipt
        ? t('e_doc_plan_satisfy_resolution_requirement_error_label')
        : undefined;
    }, [isSatisfyResolutionRequirement, isPaperReceipt, t]);

    // Image depth messages
    const depthAlertMessage = useMemo(() => {
      return !isSatisfyDepthRequirements && isPaperReceipt
        ? t('e_doc_plan_satisfy_depth_requirements_alert_label')
        : undefined;
    }, [isSatisfyDepthRequirements, isPaperReceipt, t]);

    const depthErrorMessage = useMemo(() => {
      return !isSatisfyDepthRequirements && isPaperReceipt
        ? t('e_doc_plan_satisfy_depth_requirements_error_label')
        : undefined;
    }, [isSatisfyDepthRequirements, isPaperReceipt, t]);

    // Text values
    const dpiText = useMemo(() => {
      return isScan ? dpi : '';
    }, [isScan, dpi]);

    return (
      <ListView leftRightRatio={leftRightRatio}>
        <Top>
          <ListItem
            label={t('dpi_label')}
            text={dpiText}
            type={type}
            alertMessage={dpiAlertMessage}
            errorMessage={dpiErrorMessage}
          />
        </Top>
        <Top>
          <ListItem
            label={t('pixel_number_label')}
            text={pixelNumber}
            type={type}
          />
        </Top>
        <Top>
          <ListItem
            label={t('image_depth_label')}
            text={imageDepth}
            type={type}
            alertMessage={depthAlertMessage}
            errorMessage={depthErrorMessage}
          />
        </Top>
        <Bottom>
          <ListItem label={t('timestamp')} text='付与済み' />
        </Bottom>
      </ListView>
    );
  }
);
EDocMetadata.displayName = 'EDocMetadata';

type ListItemProps = {
  label: string;
  text: string;
  type?: 'warning' | 'error';
  alertMessage?: string;
  errorMessage?: string;
};

const ListItem: FC<ListItemProps> = memo(
  ({ label, text, type, alertMessage, errorMessage }) => {
    const showCaption = useMemo(() => {
      return (
        (type === 'warning' && alertMessage) ||
        (type === 'error' && errorMessage)
      );
    }, [type, alertMessage, errorMessage]);

    const captionContent = useMemo(() => {
      return type === 'warning' ? alertMessage : errorMessage;
    }, [type, alertMessage, errorMessage]);

    return (
      <>
        <Left>
          <div className={cx(styles['label'])}>{label}</div>
        </Left>
        <Right>
          <div className={cx(styles['content'])}>
            <div>{text}</div>
            <div>
              {showCaption && (
                <Caption icon type={type === 'warning' ? 'alert' : 'error'}>
                  {captionContent}
                </Caption>
              )}
            </div>
          </div>
        </Right>
      </>
    );
  }
);
ListItem.displayName = 'ListItem';
